import type {
  ClaimStatusResponse,
  StartVerificationResponse,
  CheckVerificationResponse,
  ManualClaimResponse,
} from '@/types'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

export function useClaim(slug: string | Ref<string>) {
  const slugRef = isRef(slug) ? slug : ref(slug)

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  async function getStatus(): Promise<ClaimStatusResponse | null> {
    isLoading.value = true
    error.value = null

    try {
      // Use native fetch to ensure cookies are sent properly
      const res = await fetch(`/api/clinics/${slugRef.value}/claim/status`, {
        credentials: 'include',
      })

      if (!res.ok) {
        error.value = 'Nepodařilo se načíst stav převzetí'
        return null
      }

      const response = await res.json() as ApiResponse<ClaimStatusResponse>

      if (!response.success || !response.data) {
        error.value = 'Nepodařilo se načíst stav převzetí'
        return null
      }

      return response.data
    } catch (err) {
      const e = err as { data?: { message?: string }; message?: string }
      error.value = e.data?.message || e.message || 'Chyba při načítání stavu'
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function startVerification(phone: string): Promise<StartVerificationResponse | null> {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<ApiResponse<StartVerificationResponse>>(
        `/api/clinics/${slugRef.value}/claim/start-verification`,
        {
          method: 'POST',
          body: { phone },
          credentials: 'include',
        }
      )

      if (!response.success || !response.data) {
        error.value = 'Nepodařilo se odeslat ověřovací kód'
        return null
      }

      return response.data
    } catch (err) {
      const e = err as { data?: { message?: string }; message?: string }
      error.value = e.data?.message || e.message || 'Chyba při odesílání kódu'
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function checkVerification(code: string): Promise<CheckVerificationResponse | null> {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<ApiResponse<CheckVerificationResponse>>(
        `/api/clinics/${slugRef.value}/claim/check-verification`,
        {
          method: 'POST',
          body: { code },
          credentials: 'include',
        }
      )

      if (!response.success || !response.data) {
        error.value = 'Nepodařilo se ověřit kód'
        return null
      }

      return response.data
    } catch (err) {
      const e = err as { data?: { message?: string }; message?: string }
      error.value = e.data?.message || e.message || 'Chyba při ověření kódu'
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function requestManualClaim(
    email: string,
    message?: string
  ): Promise<ManualClaimResponse | null> {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<ApiResponse<ManualClaimResponse>>(
        `/api/clinics/${slugRef.value}/claim/request-manual`,
        {
          method: 'POST',
          body: { email, message },
          credentials: 'include',
        }
      )

      if (!response.success || !response.data) {
        error.value = 'Nepodařilo se odeslat žádost'
        return null
      }

      return response.data
    } catch (err) {
      const e = err as { data?: { message?: string }; message?: string }
      error.value = e.data?.message || e.message || 'Chyba při odesílání žádosti'
      return null
    } finally {
      isLoading.value = false
    }
  }

  function clearError() {
    error.value = null
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    getStatus,
    startVerification,
    checkVerification,
    requestManualClaim,
    clearError,
  }
}
